import React from 'react'

import { useId } from '@react-aria/utils'
import { Box } from '../../Box'
import { Breadcrumbs } from '../../Breadcrumbs'
import { Button } from '../../Button'
import { Card } from '../../Card'
import { Flex } from '../../Flex'
import { FlexList } from '../../FlexList'
import { Input } from '../../Input'
import { Modal } from '../../Modal'
import { useLabelledPopup } from '../../OverlayTrigger/a11yPresets'
import { Page } from '../../PageLayout'
import { StyledPageHeaderItem } from '../../PageLayout/PageLayout.styles'
import { Panel } from '../../Panel'
import { Required } from '../../Required'
import { H1, Label } from '../../Semantic'
import { SuperSelect } from '../../SuperSelect/SuperSelect'
import { Tabs } from '../../Tabs'
import { Tab } from '../../Tabs/Tabs'
import { exampleText } from '../../_storyHelpers_/constants'

const longText = exampleText.longest_ipsum
const shortText = exampleText.short_sentence

const panelContent = (
  <Flex
    direction="column"
    alignItems="center"
    paddingBottom="lg"
    style={{ width: '100%' }}
  >
    {Array.from({ length: 10 }).map((_, i) => (
      <div
        key={`${i}-content-item`}
        style={{
          width: '80%',
          height: '100px',
          marginTop: '16px',
          backgroundColor: 'white',
          border: '1px solid lightgray',
          borderRadius: '8px',
        }}
      />
    ))}
  </Flex>
)

function PanelSample({
  onClose,
  hasFooter: footer,
}: {
  onClose: () => void
  hasFooter?: boolean
}) {
  return (
    <Panel>
      <Panel.Header onClose={onClose}>
        <Panel.Title>Sample heading</Panel.Title>
      </Panel.Header>
      <Panel.Body>{panelContent}</Panel.Body>
      {footer && (
        <Panel.Footer>
          <Button>Action 3</Button>
          <FlexList space="sm" marginLeft="auto">
            <Button variant="primary">Action 4</Button>
            <Button onClick={onClose} variant="secondary">
              Close
            </Button>
          </FlexList>
        </Panel.Footer>
      )}
    </Panel>
  )
}

export const PageLayoutDemo = React.forwardRef<
  HTMLInputElement,
  { asideVisible?: boolean }
>(function PageLayoutDemo(props, ref) {
  const [isAsideVisible, setIsAsideVisible] = React.useState(
    props.asideVisible ?? false
  )
  const [isModalVisible, setIsModalVisible] = React.useState(false)
  const asideId = useId()

  const { triggerProps, labelProps, popupProps } = useLabelledPopup({
    isOpen: isModalVisible,
    popupRole: 'dialog',
  })

  return (
    <>
      <Modal
        open={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        {...popupProps}
        role="dialog"
        aria-modal={true}
        howToClose={['x']}
      >
        <Modal.Header {...labelProps}>{shortText}</Modal.Header>
        <Modal.Body tabIndex={0}>{longText}</Modal.Body>
      </Modal>
      <Page>
        <Page.Main>
          <Page.Header>
            <StyledPageHeaderItem>
              <Breadcrumbs variant="list">
                <Breadcrumbs.Crumb active>Tool Name</Breadcrumbs.Crumb>
                <Breadcrumbs.Crumb>Item</Breadcrumbs.Crumb>
              </Breadcrumbs>
            </StyledPageHeaderItem>
            <Page.Title>
              <H1 id="page-title">Page Title</H1>
            </Page.Title>
            <Page.Tabs>
              <Tabs>
                <Tab active>
                  <Tabs.Link>Active</Tabs.Link>
                </Tab>
                <Tab>
                  <Tabs.Link>Second</Tabs.Link>
                </Tab>
                <Tab>
                  <Tabs.Link>Third</Tabs.Link>
                </Tab>
                <Tab>
                  <Tabs.Link>Fourth</Tabs.Link>
                </Tab>
              </Tabs>
            </Page.Tabs>
          </Page.Header>
          <Page.Body>
            <Card>
              <Box padding="md">
                <SuperSelect
                  placeholder="Select"
                  block
                  multiple
                  options={[
                    { value: '1', label: 'some label one' },
                    { value: '2', label: 'two 2' },
                    { value: '3', label: 'three three three' },
                  ]}
                  onChange={console.log}
                />
              </Box>

              <Box padding="md">
                <Label block style={{ marginBottom: '4px' }} htmlFor="example">
                  Label Text
                  <Required />
                </Label>

                <Input id="example" placeholder="Hello" ref={ref} />
              </Box>
            </Card>
            <Card>
              <Box padding="md" marginTop="sm">
                {longText}
              </Box>
            </Card>
            <Card>
              <Box padding="md" marginTop="sm">
                {longText}
              </Box>
            </Card>
            <Card>
              <Box padding="md" marginTop="sm">
                {longText}
              </Box>
            </Card>
            <Card>
              <Box padding="md" marginTop="sm">
                {longText}
              </Box>
            </Card>
          </Page.Body>
          <Page.Footer>
            <Box padding="16px" display="flex">
              <Button
                {...triggerProps}
                variant="secondary"
                onClick={() => setIsModalVisible(true)}
              >
                Open Modal
              </Button>
              <Button
                aria-expanded={isAsideVisible}
                aria-controls={asideId}
                variant="secondary"
                onClick={() => setIsAsideVisible(true)}
                style={{ marginLeft: 'auto' }}
              >
                Open Panel
              </Button>
            </Box>
          </Page.Footer>
        </Page.Main>
        <Page.Aside id={asideId} open={isAsideVisible} restoreFocusOnClose>
          <PanelSample hasFooter onClose={() => setIsAsideVisible(false)} />
        </Page.Aside>
      </Page>
    </>
  )
})
